package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Slider
import androidx.compose.material3.SliderDefaults
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.runtime.LaunchedEffect
import kotlinx.coroutines.delay
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.FilterResultsBottomSheetViewModel
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.AmountInputField
import com.example.mystybag.common.ui.components.custom.ComboBox
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.setSoftInputModeOverlay
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import dev.icerock.moko.resources.compose.painterResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FilterResultsBottomSheetView(viewModel: FilterResultsBottomSheetViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    val scrollState = rememberScrollState()
    val colors = com.example.mystybag.core.views.AppTheme.current.colors
    val typography = com.example.mystybag.core.views.AppTheme.current.typography

    DisposableEffect(Unit) {
        setSoftInputModeOverlay(overlay = true)
        onDispose { setSoftInputModeOverlay(overlay = false) }
    }
    LaunchedEffect(Unit) {
        delay(150)
        setSoftInputModeOverlay(overlay = true)
    }

    BoxWithConstraints(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.TopCenter
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .fillMaxWidth()
                .background(colors.surface)
        ) {
            // Approximate height of fixed bottom bar (Reset + Apply + padding)
            val webBottomActionInset = if (PlatformHelper.isWeb) 24.dp else 0.dp
            val bottomBarHeight = 112.dp + webBottomActionInset
            Column(
                modifier = Modifier
                    .height(maxHeight - bottomBarHeight)
                    .fillMaxWidth()
                    .verticalScroll(scrollState)
            ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = p16)
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(48.dp)
                    .padding(top = p12, bottom = p12)
            ) {
                Text(
                    modifier = Modifier.align(Alignment.Center),
                    text = sharedString(SR.strings.filter),
                    style = typography.titleLarge.copy(fontWeight = FontWeight.SemiBold),
                    color = colors.onSurface,
                    textAlign = TextAlign.Center
                )
                Box(
                    modifier = Modifier
                        .align(Alignment.CenterEnd)
                        .size(24.dp)
                        .rippleClick { viewModel.closeBottomSheet() },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(SRC.images.close_button),
                        modifier = Modifier.size(16.dp),
                        colorFilter = ColorFilter.tint(colors.onSurface)
                    )
                }
            }
            Column(modifier = Modifier.padding(top = p12)) {
                Column() {
                    Texts.TitleMedium(modifier = Modifier.padding(bottom = p6), text = sharedString(SR.strings.distance), color = colors.onSurface)
                    Texts.BodyMedium(modifier = Modifier.padding(bottom = p12), text = sharedString(SR.strings.pickup_location_distance_from_me), color = colors.onSurfaceVariant)
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = p12),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Switch(
                        checked = uiState.useMyLocation,
                        onCheckedChange = viewModel::setUseMyLocation,
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = colors.onPrimary,
                            checkedTrackColor = colors.primary,
                            uncheckedThumbColor = colors.surface,
                            uncheckedTrackColor = colors.surfaceContainerHighest
                        )
                    )
                    Texts.TitleMedium(
                        modifier = Modifier.padding(start = p12),
                        text = sharedString(SR.strings.use_my_location),
                        color = colors.onSurface
                    )
                }

                Column() {
                    val sliderColors = SliderDefaults.colors(
                        thumbColor = colors.surface,
                        activeTrackColor = colors.primary,
                        inactiveTrackColor = colors.surfaceContainerHighest,
                        activeTickColor = colors.primary,
                        inactiveTickColor = colors.outlineVariant
                    )
                    Slider(
                        value = uiState.distanceKm,
                        onValueChange = viewModel::setDistanceKm,
                        valueRange = 1f..40f,
                        steps = 38,
                        modifier = Modifier.fillMaxWidth(),
                        colors = sliderColors,
                        track = { sliderState ->
                            SliderDefaults.Track(
                                modifier = Modifier.height(p2),
                                sliderState = sliderState,
                                colors = sliderColors,
                                thumbTrackGapSize = 0.dp
                            )
                        },
                        thumb = {
                            Box(
                                modifier = Modifier
                                    .size(40.dp)
                                    .shadow(2.dp, CircleShape),
                                contentAlignment = Alignment.Center
                            ) {
                                Image(
                                    painter = painterResource(SRC.images.slider_thumb),
                                    modifier = Modifier.size(40.dp)
                                )
                            }
                        }
                    )
                    Text(
                        text = if (uiState.distanceKm < 3f) "<3 km" else "${uiState.distanceKm.toInt()} km",
                        style = typography.bodyMedium,
                        color = colors.onSurfaceVariant,
                        modifier = Modifier.padding(top = p6)
                    )
                }
            }

            // ——— Merchant locations (obekti) ———
            if (viewModel.availableLocations.isNotEmpty()) {
                Column(modifier = Modifier.padding(top = p40)) {
                    Column(modifier = Modifier.padding(bottom = p12)) {
                        Texts.TitleMedium(text = sharedString(SR.strings.merchant), color = colors.onSurface)
                    }
                    val locationOptions = listOf<Pair<Long?, String>>(null to sharedString(SR.strings.all_locations)) +
                        viewModel.availableLocations.map { (id, name) -> id to name.ifBlank { "#$id" } }
                    val selectedLocationOption = locationOptions.find { it.first == uiState.selectedLocationId } ?: locationOptions.first()
                    ComboBox(
                        modifier = Modifier.fillMaxWidth(),
                        value = selectedLocationOption,
                        items = locationOptions,
                        onValueChange = { viewModel.setSelectedLocationId(it.first) },
                        label = sharedString(SR.strings.filter_location_select_hint),
                        displayText = { it.second },
                        useRightArrow = false,
                        backgroundColor = colors.surface,
                        borderColor = colors.outline,
                        trailingIconSize = p16
                    )
                }
            }

            // ——— Price range ———
            Column(modifier = Modifier.padding(top = p40)) {
                Column(modifier = Modifier.padding(bottom = p12)) {
                    Texts.TitleMedium(text = sharedString(SR.strings.price_range), color = colors.onSurface)
                    Texts.BodyMedium(text = sharedString(SR.strings.discounted_price_of_the_bag), color = colors.onSurfaceVariant)
                }
                BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                    val fieldWidth = (maxWidth - 48.dp) / 2
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        FilterPriceField(
                            label = sharedString(SR.strings.minimum),
                            value = uiState.priceMin,
                            onValueChange = viewModel::setPriceMin,
                            sanitize = viewModel::sanitizePriceInput,
                            onMaxPriceExceeded = viewModel::showPriceMaxExceededMessage,
                            modifier = Modifier.width(fieldWidth)
                        )
                        FilterPriceField(
                            label = sharedString(SR.strings.maximum),
                            value = uiState.priceMax,
                            onValueChange = viewModel::setPriceMax,
                            sanitize = viewModel::sanitizePriceInput,
                            onMaxPriceExceeded = viewModel::showPriceMaxExceededMessage,
                            modifier = Modifier.width(fieldWidth)
                        )
                    }
                }
            }

            // ——— Merchant rating ———
            Column(modifier = Modifier.padding(top = p40)) {
                Texts.TitleMedium(modifier = Modifier.padding(bottom = p12), text = sharedString(SR.strings.merchant_rating), color = colors.onSurface)
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    listOf(
                        "3.0+",
                        "4.0+",
                        "4.5+"
                    ).forEachIndexed { index, label ->
                        FilterChipWithStar(
                            text = label,
                            selected = uiState.selectedRatingIndex == (index + 1),
                            onClick = { viewModel.setSelectedRatingIndex(index + 1) }
                        )
                    }
                }
            }

            Column(modifier = Modifier.padding(top = p40)) {
                Texts.TitleMedium(modifier = Modifier.padding(bottom = p12), text = sharedString(SR.strings.offer_published_date), color = colors.onSurface)
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    listOf(
                        sharedString(SR.strings.today),
                        sharedString(SR.strings.last_3_days),
                        sharedString(SR.strings.this_week),
                        sharedString(SR.strings.this_month)
                    ).forEachIndexed { index, label ->
                        FilterChip(
                            text = label,
                            selected = uiState.selectedPublishedDateIndex == index,
                            onClick = { viewModel.setSelectedPublishedDateIndex(index) }
                        )
                    }
                }
            }

            Column(modifier = Modifier.padding(top = p40)) {
                Texts.TitleMedium(modifier = Modifier.padding(bottom = p12),text = sharedString(SR.strings.collection_time), color = colors.onSurface)
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    listOf(
                        sharedString(SR.strings.available_now),
                        "08:00-12:00",
                        "12:00-17:00",
                        sharedString(SR.strings.evening)
                    ).forEachIndexed { index, label ->
                        FilterChip(
                            text = label,
                            selected = uiState.selectedCollectionTimeIndex == (index + 1),
                            onClick = { viewModel.setSelectedCollectionTimeIndex(index + 1) }
                        )
                    }
                }
                Spacer(modifier = Modifier.height(p24))
            }
        }
        }

            Column(
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .fillMaxWidth()
                    .background(colors.surface)
                    .padding(horizontal = p16)
                    .padding(bottom = webBottomActionInset)
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = p8)
                        .rippleClick { viewModel.onResetFiltersClick() }
                ) {
                    Texts.BodyMedium(
                        modifier = Modifier
                            .fillMaxWidth(),
                        text = sharedString(SR.strings.reset_filters),
                        color = colors.onSurfaceVariant,
                        textDecoration = androidx.compose.ui.text.style.TextDecoration.Underline,
                        textAlign = TextAlign.Center
                    )
                }
                DefaultButton(
                    modifier = Modifier.fillMaxWidth(),
                    caption = sharedString(SR.strings.apply),
                    callback = viewModel::onApplyClick
                )
                Spacer(modifier = Modifier.height(p12))
            }
    }
}
}

private const val FILTER_PRICE_MAX = 999.99

@Composable
private fun FilterPriceField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    sanitize: (String) -> String,
    onMaxPriceExceeded: (() -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    AmountInputField(
        value = value,
        onValueChange = { newText ->
            val filtered = sanitize(newText)
            val parsed = filtered.toDoubleOrNull()
            if (parsed != null && parsed > FILTER_PRICE_MAX) {
                onMaxPriceExceeded?.invoke()
                return@AmountInputField
            }
            onValueChange(filtered)
        },
        placeholder = "0.00",//sharedString(SR.strings.enter_amount),
        label = label,
        modifier = modifier
    )
}

@Composable
private fun FilterChip(
    text: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    val colors = com.example.mystybag.core.views.AppTheme.current.colors
    val chipShape = RoundedCornerShape(57.dp)
    Box(
        modifier = Modifier
            .clip(chipShape)
            .defaultMinSize(minHeight = 36.dp)
            .then(
                if (selected)
                    Modifier.background(colors.primary)
                else
                    Modifier
                        .background(Color.White)
                        .border(1.dp, colors.outline, chipShape)
            )
            .rippleClick(action = onClick),
        contentAlignment = Alignment.Center
    ) {
        Text(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            text = text,
            style = com.example.mystybag.core.views.AppTheme.current.typography.titleSmall,
            color = if (selected) colors.onPrimary else colors.onSurfaceVariant,
            maxLines = 1
        )
    }
}

@Composable
private fun FilterChipWithStar(
    text: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    val colors = com.example.mystybag.core.views.AppTheme.current.colors
    val chipShape = RoundedCornerShape(57.dp)
    Box(
        modifier = Modifier
            .clip(chipShape)
            .defaultMinSize(minHeight = 36.dp)
            .then(
                if (selected)
                    Modifier.background(colors.primary)
                else
                    Modifier
                        .background(Color.White)
                        .border(1.dp, colors.outline, chipShape)
            )
            .rippleClick(action = onClick),
        contentAlignment = Alignment.Center
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Image(
                painter = painterResource(SRC.images.star_icon),
                modifier = Modifier.size(17.dp, 16.dp),
                colorFilter = ColorFilter.tint(if (selected) colors.onPrimary else colors.onSurfaceVariant)
            )
            Text(
                text = text,
                style = com.example.mystybag.core.views.AppTheme.current.typography.titleSmall,
                color = if (selected) colors.onPrimary else colors.onSurfaceVariant
            )
        }
    }
}
